"use client"

import useEmblaCarousel from "embla-carousel-react"
import { HttpTypes } from "@medusajs/types"
import Image from "next/image"
import { ProductCarouselIndicator } from "@/components/molecules"
import { useScreenSize } from "@/hooks/useScreenSize"
import { useTranslate } from "@tolgee/react"
import { useEffect, useRef } from "react"

export const ProductCarousel = ({
  slides = [],
}: {
  slides: HttpTypes.StoreProduct["images"]
}) => {
  const screenSize = useScreenSize()
  const { t } = useTranslate()

  const [emblaRef, emblaApi] = useEmblaCarousel({
    axis: screenSize === "sm" ? "x" : "y",
    loop: true,
    align: "start",
  })


  const itemRefs = useRef<(HTMLVideoElement | null)[]>([]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', emblaApiSelect);
    return () => {
      emblaApi.off('select', emblaApiSelect);
    };
  }, [emblaApi])

  const emblaApiSelect = () => {
    const selectedIndex = emblaApi!.selectedScrollSnap();
    scrollToEnd(selectedIndex);
  }

  const scrollToEnd = (idx: number) => {
    itemRefs.current.forEach((v, i) => {
      if (i === idx) {
        v?.play()
      } else {
        v?.pause()
      }
    })
  }

  return (
    <div className="embla relative flex md:flex-row flex-col md:gap-4 md:items-start">
      {/* Thumbnail indicators on the left for desktop */}
      {slides?.length ? (
        <ProductCarouselIndicator slides={slides} embla={emblaApi} scrollToEnd={scrollToEnd} />
      ) : null}

      {/* Main image */}
      <div
        className="embla__viewport overflow-hidden flex-1"
        ref={emblaRef}
      >
        <div className="h-[363px] xl:h-[511px] 2xl:h-[682px] flex md:block">
          {(slides || []).map((slide, index) => (
            <div
              key={slide.id}
              className="embla__slide min-w-0 h-full"
            >
              {slide.url.endsWith(".mp4") ?
                <video ref={(el) => {
                  itemRefs.current[index] = el
                }} controls className="w-full h-full object-cover rounded-lg" src={decodeURIComponent(slide.url)} />
                : <Image
                  src={decodeURIComponent(slide.url)}
                  alt={t("product_carousel.image_alt")}
                  width={700}
                  height={700}
                  quality={100}
                  className="w-full h-full object-cover rounded-lg"
                />}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
